import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { NavItem } from 'react-bootstrap';
import { Icon } from 'modules/core/components';
import { ICONS_HEIGHT, STATIC_IMAGE_URLS } from 'modules/core/constants';
import { AppDispatch } from 'store/store';
import { getFullSideBar, getDisabledFeaturesList, getFetchingUIFlag } from 'store/selectors/ui.selector';
import { getFetchingLendersFlag, getLendersList, getLenderState } from 'store/selectors/lenders.selector';
import { setFullSideBar } from 'store/reducers/ui.reducer';
import { PermissionWrapper } from 'modules/PermissionWrapper';
import { ALL } from 'modules/PermissionWrapper/roleGroups.const';
import { useWindowDimensions } from 'hooks/useWindowDimensions';
import { getAuthState, getUser } from 'store/selectors/auth.selector';
import { useDebounceForLoading } from 'router/utils/useDebounceForLoading';
import { getDealSourceData } from 'store/selectors/RACAffordabilityEngine.selector';
import { ROUTER_PATH } from 'router/const/routerPath.const';
import { useModalState } from 'modules/core/hooks';
import { MODAL_DIALOG_ID } from 'modules/core/constants';
import { Roles } from 'modules/core/enums/roles.enum';

import {
  MenuItemTitle,
  SideBarList,
  SideBarListLink,
  SideBarListExternalLink,
  SideBarMenu,
  ToggleMenuIcon,
  Overlay,
  SideBarHeader,
} from './styles';
import { IMenuLink, defaultMenuLinks } from './sideBarMenuLinks';
import {
  filterByDisabledFeatures,
  concatMenuLinks,
  filterForSsoUser,
  getShopByBudgetPlusLinks,
  filterLinksBySalesPersonAccess,
  getShopByCallbackPlusLinks,
} from './utils';
import { useClickOutsideSideBar } from './hooks';

type NavigationPropsType = { to?: string; href?: string; selected?: boolean };
interface INavigationLink {
  $smallFont?: boolean;
}

interface ISideBar {
  isExternal?: boolean;
}

export const SideBar: React.FC<ISideBar> = ({ isExternal }) => {
  const { isModalDialogActive } = useModalState([MODAL_DIALOG_ID.LOAN_VARIABLES]);
  const dispatch = useDispatch<AppDispatch>();
  const fullSideBar = useSelector(getFullSideBar);
  const user = useSelector(getUser);
  const lenderState = useSelector(getLenderState);
  const racDealSourceData = useSelector(getDealSourceData);
  const lenderPath = lenderState?.dealSourceData?.lender_path || racDealSourceData?.lender_path;
  const disabledFeaturesList = useSelector(getDisabledFeaturesList);
  const lenderLinks = useSelector(getLendersList);
  const { pathname } = window.location;
  const { t } = useTranslation();
  const currentPathName = useMemo(() => pathname, [pathname]);

  const shopByBudgetPlusLinks = getShopByBudgetPlusLinks(lenderLinks);
  const shopByCallbackPlusLinks = getShopByCallbackPlusLinks(lenderLinks);

  const isShopByBudgetPlusSelected = useMemo(() => {
    let isSelected = false;

    if (shopByBudgetPlusLinks.length > 0) {
      shopByBudgetPlusLinks.forEach((link: IMenuLink) => {
        if (link.link && currentPathName.includes(link.link)) {
          isSelected = true;
        }
      });
    }

    return isSelected;
  }, [shopByBudgetPlusLinks, currentPathName]);

  const isShopByCallbackPlusSelected = useMemo(() => {
    let isSelected = false;

    if (shopByCallbackPlusLinks.length > 0) {
      shopByCallbackPlusLinks.forEach((link: IMenuLink) => {
        if (link.link && currentPathName.includes(link.link)) {
          isSelected = true;
        }
      });
    }

    return isSelected;
  }, [shopByCallbackPlusLinks, currentPathName]);

  const enabledDefaultMenuLinks = filterByDisabledFeatures(defaultMenuLinks, disabledFeaturesList);
  const enabledLendersMenuLinks = filterByDisabledFeatures(lenderLinks, disabledFeaturesList);

  const roleAwareLenderLinks =
    user?.role === Roles.SALESPERSON
      ? filterLinksBySalesPersonAccess(enabledLendersMenuLinks, true)
      : enabledLendersMenuLinks;

  const hasSalespersonLendersLinks = user?.role === Roles.SALESPERSON ? roleAwareLenderLinks.length > 0 : true;

  // Without MIW - Without 'Make It Work' section
  const defaultLinksWithoutMIWForSales =
    user?.role === Roles.SALESPERSON && !hasSalespersonLendersLinks
      ? enabledDefaultMenuLinks.filter((item) => item.link !== ROUTER_PATH.RAC_AFFORDABILITY_ENGINE)
      : enabledDefaultMenuLinks;

  const enabledMenuLinks = filterForSsoUser(
    concatMenuLinks(
      shopByBudgetPlusLinks,
      shopByCallbackPlusLinks,
      defaultLinksWithoutMIWForSales,
      roleAwareLenderLinks,
    ),
    user,
    lenderPath,
  );

  const { isLargeWidth } = useWindowDimensions();
  const { isFetching: isAuthFetching } = useSelector(getAuthState);
  const isFetchingFeatureList = useSelector(getFetchingUIFlag);
  const isFetchingLendersList = useSelector(getFetchingLendersFlag);
  const isFetching = isAuthFetching || isFetchingFeatureList || isFetchingLendersList;

  const isLoading = useDebounceForLoading(isFetching, isFetching);

  const sidebarRef = useRef(null);

  useClickOutsideSideBar(sidebarRef, fullSideBar);

  const toggleBodyScroll = (hide: boolean) => {
    document.body.style.overflow = hide ? 'hidden' : 'auto';
  };

  const toggleSideBar = useCallback(() => {
    if (isLargeWidth && !fullSideBar) {
      toggleBodyScroll(true);
    } else if (!isModalDialogActive) {
      toggleBodyScroll(false);
    }

    dispatch(setFullSideBar(!fullSideBar));
  }, [dispatch, fullSideBar, isLargeWidth, isModalDialogActive]);

  useEffect(() => {
    dispatch(setFullSideBar(false));

    if (!isModalDialogActive) {
      toggleBodyScroll(false);
    }
  }, [pathname, dispatch, isModalDialogActive]);

  return isLoading ? (
    <></>
  ) : (
    <>
      <Overlay open={fullSideBar && isLargeWidth} onClick={toggleSideBar} />
      <SideBarMenu ref={sidebarRef} open={fullSideBar} className="sidebarMenu">
        <ToggleMenuIcon
          isExternal={isExternal}
          open={fullSideBar}
          onClick={toggleSideBar}
          id="sidebar-arrow"
          data-testid="sideBarArrow"
        >
          <SideBarHeader open={fullSideBar}>{t('common.sideBarMenuLink.title')}</SideBarHeader>
          <span />
        </ToggleMenuIcon>
        <div>
          <SideBarList open={fullSideBar}>
            {enabledMenuLinks.map((item: IMenuLink) => {
              const NavigationLink: FC<INavigationLink> =
                item.link && !isExternal ? SideBarListLink : SideBarListExternalLink;

              const isLenderPath =
                currentPathName === ROUTER_PATH.RAC_AFFORDABILITY_ENGINE ||
                currentPathName.includes(ROUTER_PATH.LENDER.split(':')[0]);
              const isLenderSelected =
                item.name === t('common.sideBarMenuLink.makeItWork') &&
                isLenderPath &&
                !isShopByBudgetPlusSelected &&
                !isShopByCallbackPlusSelected;

              let navigationProps: NavigationPropsType = {
                href: item.href || item.link,
                selected: isLenderSelected || currentPathName.includes(item.href as string),
              };

              if (item.link && !isExternal) {
                navigationProps = { to: item.link, selected: item.link === currentPathName };
              }

              return (
                <PermissionWrapper allowedRoles={item.allowedRoles || ALL} key={`${item.name}-${Math.random()}`}>
                  {!(!fullSideBar && !item.iconUrl) && (
                    <NavItem
                      key={item.name}
                      onMouseDown={item?.iconUrl === STATIC_IMAGE_URLS.sideBar.rac ? toggleSideBar : undefined}
                    >
                      <NavigationLink $smallFont={item.smallFont} {...navigationProps}>
                        <Icon imageSrc={item?.iconUrl} height={ICONS_HEIGHT.DEFAULT} />
                        <MenuItemTitle open={fullSideBar} $smallFont={item.smallFont}>
                          {item.uiName || item.name}
                        </MenuItemTitle>
                      </NavigationLink>
                    </NavItem>
                  )}
                </PermissionWrapper>
              );
            })}
          </SideBarList>
        </div>
      </SideBarMenu>
    </>
  );
};
